import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listStores, getGuestlistReport } from "@/lib/api/admin";
import type { Store, GuestlistReportRow, GuestlistReportDay } from "@/lib/api/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/DataTable";
import type { Column } from "@/components/DataTable";
import { Spinner, SpinnerCenter } from "@/components/Spinner";
import { cn } from "@/lib/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const AURA_ID = "642e5bba-117f-4d14-9261-d1f5a0145bbb";
const AURA_DEFAULT: Store = {
  id: AURA_ID,
  name: "Aura Nightclub",
  category: "nightclub",
  status: "active",
  price_limit_bypass: false,
  events_enabled: false,
  appointments_enabled: false,
  approved_at: "",
  visible: true,
  created_at: "",
  updated_at: "",
};

const TOTAL_ROW_ID = "__total__";

type DatePreset = "3mo" | "6mo" | "12mo" | "custom";

// ─── Utilities ────────────────────────────────────────────────────────────────

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function computeDateRange(
  preset: DatePreset,
  customFrom: string,
  customTo: string
): { startDate: string; endDate: string } {
  const today = toDateInputValue(new Date());
  if (preset === "custom") {
    return { startDate: customFrom || today, endDate: customTo || today };
  }
  const months = { "3mo": 3, "6mo": 6, "12mo": 12 }[preset];
  const start = new Date();
  start.setMonth(start.getMonth() - months);
  return { startDate: toDateInputValue(start), endDate: today };
}

function formatWeekPeriod(periodStart: string): string {
  const d = new Date(`${periodStart}T00:00:00`);
  return `Week of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

function formatMonthPeriod(periodStart: string): string {
  const d = new Date(`${periodStart}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function formatCutoffLabel(cutoff: string): string {
  const [hh, mm] = cutoff.split(":").map(Number);
  const d = new Date(2000, 0, 1, hh, mm);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function inviterName(row: GuestlistReportRow): string {
  const name = `${row.first_name} ${row.last_name}`.trim();
  return name || row.inviter_uid;
}

const PAYOUT_PER_GUEST_DOLLARS = 5;

function estimatedCost(row: GuestlistReportRow): number {
  return (row.total - row.at_after_cutoff2) * PAYOUT_PER_GUEST_DOLLARS;
}

// Tiered on the promoter's whole scanned headcount (not marginal): 1-9 guests pay $3 each, 10+ pay $5 each for all of them.
const PAYOUT_TIER_THRESHOLD = 10;
const PAYOUT_LOW_TIER_DOLLARS = 3;
const PAYOUT_HIGH_TIER_DOLLARS = 5;

function payoutCost(row: GuestlistReportRow): number {
  const guests = row.total_incl_extra;
  const rate = guests >= PAYOUT_TIER_THRESHOLD ? PAYOUT_HIGH_TIER_DOLLARS : PAYOUT_LOW_TIER_DOLLARS;
  return guests * rate;
}

// Payout is tiered per promoter, so the totals row must sum each row's payout rather than tier the grand total.
type ReportRow = GuestlistReportRow & { payout_cost: number };

function formatPayout(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}

function betweenCutoffs(row: GuestlistReportRow): number {
  return row.at_after_cutoff1 - row.at_after_cutoff2;
}

function distinctPeriodsDesc(rows: GuestlistReportRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.period_start)))
    .sort()
    .reverse();
}

function withTotalsRow(apiRows: GuestlistReportRow[]): ReportRow[] {
  const rows = apiRows.map((r) => ({ ...r, payout_cost: payoutCost(r) }));
  const totals = rows.reduce(
    (acc, r) => ({
      total: acc.total + r.total,
      before_cutoff1: acc.before_cutoff1 + r.before_cutoff1,
      at_after_cutoff1: acc.at_after_cutoff1 + r.at_after_cutoff1,
      at_after_cutoff2: acc.at_after_cutoff2 + r.at_after_cutoff2,
      additional_guest_count: acc.additional_guest_count + r.additional_guest_count,
      total_incl_extra: acc.total_incl_extra + r.total_incl_extra,
      payout_cost: acc.payout_cost + r.payout_cost,
    }),
    {
      total: 0,
      before_cutoff1: 0,
      at_after_cutoff1: 0,
      at_after_cutoff2: 0,
      additional_guest_count: 0,
      total_incl_extra: 0,
      payout_cost: 0,
    }
  );
  const totalRow: ReportRow = {
    inviter_uid: TOTAL_ROW_ID,
    first_name: "",
    last_name: "",
    period_start: "",
    ...totals,
  };
  return rows.length > 0 ? [totalRow, ...rows] : rows;
}

function buildColumns(
  nameHeader: string,
  cutoff1Label: string,
  cutoff2Label: string,
  isPromoter: boolean
): Column<ReportRow>[] {
  const cell = (r: ReportRow, value: React.ReactNode) =>
    r.inviter_uid === TOTAL_ROW_ID ? <span className="font-semibold">{value}</span> : value;

  const columns: Column<ReportRow>[] = [
    {
      key: "inviter",
      header: nameHeader,
      render: (r) => cell(r, r.inviter_uid === TOTAL_ROW_ID ? "Total" : inviterName(r)),
    },
    {
      key: "total_incl_extra",
      header: "Total",
      render: (r) => cell(r, r.total_incl_extra),
    },
    {
      key: "before_cutoff1",
      header: `Before ${cutoff1Label}`,
      render: (r) => cell(r, r.before_cutoff1),
    },
    {
      key: "between_cutoffs",
      header: `Between ${cutoff1Label}–${cutoff2Label}`,
      render: (r) => cell(r, betweenCutoffs(r)),
    },
    {
      key: "at_after_cutoff2",
      header: `At/After ${cutoff2Label}`,
      render: (r) => cell(r, r.at_after_cutoff2),
    },
    {
      key: "additional_guest_count",
      header: "Additional Guests",
      render: (r) => cell(r, r.additional_guest_count),
    },
  ];

  if (isPromoter) {
    columns.push(
      {
        key: "estimated_cost",
        header: "Estimated Cost",
        render: (r) => cell(r, formatPayout(estimatedCost(r))),
      },
      {
        key: "payout_cost",
        header: "Payout Cost",
        render: (r) => cell(r, formatPayout(r.payout_cost)),
      }
    );
  }

  return columns;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function GuestlistReportPage() {
  const user = useAdminAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPromoter = searchParams.get("is_promoter") !== "false";

  // Store selection
  const [storeSearch, setStoreSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState<Store>(AURA_DEFAULT);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Date range
  const [preset, setPreset] = useState<DatePreset>("3mo");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Day of week
  const [day, setDay] = useState<GuestlistReportDay>("friday");

  // Selected period per table
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowStoreDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const { data: storeResults, isFetching: isStoreSearchFetching } = useQuery({
    queryKey: ["stores-search", storeSearch],
    queryFn: () => listStores(user!, { name: storeSearch, limit: 10 }),
    enabled: !!user && storeSearch.length > 0,
    placeholderData: keepPreviousData,
  });

  const filteredStores = storeResults?.data ?? [];

  const { startDate, endDate } = useMemo(
    () => computeDateRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const {
    data: report,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["guestlist-report", isPromoter, selectedStore.id, startDate, endDate],
    queryFn: () =>
      getGuestlistReport(user!, selectedStore.id, {
        startDate,
        endDate,
        isPromoter,
      }),
    enabled: !!user,
  });

  const dayResult = report ? report[day] : undefined;

  const weeklyPeriods = useMemo(() => distinctPeriodsDesc(dayResult?.weekly ?? []), [dayResult]);
  const monthlyPeriods = useMemo(() => distinctPeriodsDesc(dayResult?.monthly ?? []), [dayResult]);

  useEffect(() => {
    if (weeklyPeriods.length > 0 && !weeklyPeriods.includes(selectedWeek)) {
      setSelectedWeek(weeklyPeriods[0]);
    }
  }, [weeklyPeriods, selectedWeek]);

  useEffect(() => {
    if (monthlyPeriods.length > 0 && !monthlyPeriods.includes(selectedMonth)) {
      setSelectedMonth(monthlyPeriods[0]);
    }
  }, [monthlyPeriods, selectedMonth]);

  const weeklyRows = useMemo(
    () => withTotalsRow((dayResult?.weekly ?? []).filter((r) => r.period_start === selectedWeek)),
    [dayResult, selectedWeek]
  );
  const monthlyRows = useMemo(
    () => withTotalsRow((dayResult?.monthly ?? []).filter((r) => r.period_start === selectedMonth)),
    [dayResult, selectedMonth]
  );

  const nameHeader = isPromoter ? "Promoter" : "Inviter";
  const cutoff1Label = dayResult ? formatCutoffLabel(dayResult.cutoff1) : "";
  const cutoff2Label = dayResult ? formatCutoffLabel(dayResult.cutoff2) : "";

  const columns = useMemo(
    () => buildColumns(nameHeader, cutoff1Label, cutoff2Label, isPromoter),
    [nameHeader, cutoff1Label, cutoff2Label, isPromoter]
  );

  function handleRowClick(row: ReportRow) {
    if (row.inviter_uid === TOTAL_ROW_ID) return;
    void navigate(`/users/${row.inviter_uid}`);
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isPromoter ? "Promoter Report" : "Non-Promoter Report"}
        </h1>
        <p className="text-sm text-muted-foreground">
          Guest check-ins by {nameHeader.toLowerCase()}, weekly and monthly
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* View toggle */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">View</label>
            <div className="flex rounded-md border border-input overflow-hidden w-fit">
              {(
                [
                  { value: true, label: "Promoters" },
                  { value: false, label: "Non-Promoters" },
                ] as { value: boolean; label: string }[]
              ).map(({ value, label }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setSearchParams({ is_promoter: String(value) })}
                  className={cn(
                    "px-3 py-1.5 text-sm transition-colors",
                    isPromoter === value ? "bg-primary text-white" : "bg-background hover:bg-muted"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Store selector */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Store</label>
            <div className="relative max-w-sm" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Search stores…"
                  value={storeSearch}
                  onChange={(e) => {
                    setStoreSearch(e.target.value);
                    setShowStoreDropdown(true);
                  }}
                  onFocus={() => setShowStoreDropdown(true)}
                />
              </div>
              {selectedStore && !storeSearch && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Selected:{" "}
                  <span className="font-medium text-foreground">{selectedStore.name}</span>
                </p>
              )}
              {showStoreDropdown && storeSearch.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md overflow-hidden">
                  {isStoreSearchFetching && filteredStores.length === 0 ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <Spinner className="size-3.5 border-muted-foreground" />
                      Searching…
                    </div>
                  ) : filteredStores.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No stores found.</p>
                  ) : (
                    filteredStores.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex w-full items-center px-3 py-2 text-sm hover:bg-muted"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedStore(s);
                          setStoreSearch("");
                          setShowStoreDropdown(false);
                        }}
                      >
                        {s.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Date range preset */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Date Range</label>
            <div className="flex flex-wrap gap-2">
              {(["3mo", "6mo", "12mo", "custom"] as DatePreset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPreset(p)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm transition-colors",
                    preset === p
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-background hover:bg-muted"
                  )}
                >
                  {p === "custom" ? "Custom" : `Last ${p.replace("mo", " months")}`}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-auto"
                />
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-auto"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {(isLoading || isFetching) && !report && <SpinnerCenter />}

      {report && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Day</span>
            <div className="flex rounded-md border border-input overflow-hidden w-fit">
              {(
                [
                  { value: "friday", label: "Friday" },
                  { value: "saturday", label: "Saturday" },
                ] as { value: GuestlistReportDay; label: string }[]
              ).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setDay(value)}
                  className={cn(
                    "px-3 py-1.5 text-sm transition-colors",
                    day === value ? "bg-primary text-white" : "bg-background hover:bg-muted"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-4">
              <CardTitle className="text-base">By Week</CardTitle>
              {weeklyPeriods.length > 0 && (
                <select
                  className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                >
                  {weeklyPeriods.map((p) => (
                    <option key={p} value={p}>
                      {formatWeekPeriod(p)}
                    </option>
                  ))}
                </select>
              )}
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={weeklyRows}
                rowKey={(r) => `${r.inviter_uid}-${r.period_start}`}
                onRowClick={handleRowClick}
                isRowClickable={(r) => r.inviter_uid !== TOTAL_ROW_ID}
                emptyMessage="No check-ins in this date range."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-4">
              <CardTitle className="text-base">By Month</CardTitle>
              {monthlyPeriods.length > 0 && (
                <select
                  className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {monthlyPeriods.map((p) => (
                    <option key={p} value={p}>
                      {formatMonthPeriod(p)}
                    </option>
                  ))}
                </select>
              )}
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={monthlyRows}
                rowKey={(r) => `${r.inviter_uid}-${r.period_start}`}
                onRowClick={handleRowClick}
                isRowClickable={(r) => r.inviter_uid !== TOTAL_ROW_ID}
                emptyMessage="No check-ins in this date range."
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
